<template>
  <AppBody :subtitle="$t('pos-types.title')" :actions="actions">
    <CardModule untitle>
      <ExpansionPanelFilters>
        <v-form @submit.prevent="filterPosTypes" class="search-forms">
          <v-row>
            <v-col cols="12" md="3" sm="4">
              <BaseTextField
                v-model="filter.description"
                :label="$t('pos-types.description')"
                :placeholder="$t('pos-types.insert-description')"
                clearable
              ></BaseTextField>
            </v-col>
            <v-col cols="12" md="3" sm="4">
              <BaseSelect
                v-model="filter.status"
                label="Status"
                :placeholder="$t('pos.select-status')"
                :persistent-placeholder="false"
                :items="status"
                item-text="text"
                item-value="value"
                clearable
              ></BaseSelect>
            </v-col>
            <v-col cols="12" md="3" sm="4">
              <DatePicker
                :label="$t('pos.initial-inclusion-date')"
                placeholder="dd/mm/aaaa"
                @formatted-date="handleInitialDate"
                clearable
              />
            </v-col>
            <v-col cols="12" md="3" sm="4">
              <DatePicker
                :label="$t('pos.final-inclusion-date')"
                placeholder="dd/mm/aaaa"
                @formatted-date="handleFinalDate"
                clearable
              />
            </v-col>
            <v-col class="d-flex justify-end">
              <BaseButton variant="filter" type="submit">
                {{ $t("common.filter") }}
              </BaseButton>
            </v-col>
          </v-row>
        </v-form>
      </ExpansionPanelFilters>

      <v-data-table
        class="table-body"
        :no-data-text="$t('common.no-results-found')"
        :loading-text="$t('common.searching-info')"
        :loading="posTypesLoading"
        :headers="headers"
        :items="posTypes"
        :items-per-page="10"
        :footer-props="{
          itemsPerPageOptions: [10, 20, 50],
          itemsPerPageText: $t('pos-types.pos-types-per-page'),
        }"
      >
        <template v-slot:item.ins_dthr="{ item }">
          {{ formatDate(item.ins_dthr) }}
        </template>
        <template v-slot:item.ativo="{ item }">
          <v-chip small :color="formatStatus(item.ativo).color">
            {{ formatStatus(item.ativo).text }}
          </v-chip>
        </template>
        <template v-slot:item.actions="{ item }">
          <div style="display: flex; justify-content: center">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  :color="$config.dados.corinputs"
                  height="30"
                  width="30"
                  small
                  icon
                  :loading="item.edit_button_loading"
                  v-bind="attrs"
                  v-on="on"
                  @click="openEditPosTypeModal(item)"
                  class="mr-2"
                >
                  <v-icon>mdi-pencil-outline</v-icon>
                </v-btn>
              </template>
              <span>{{ $t("common.edit") }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  :color="$config.dados.corinputs"
                  height="30"
                  v-bind="attrs"
                  v-on="on"
                  width="30"
                  small
                  icon
                  @click="openDeleteModal(item)"
                >
                  <v-icon>mdi-delete-outline</v-icon>
                </v-btn>
              </template>
              <span>{{ $t("common.delete") }}</span>
            </v-tooltip>
          </div>
        </template>
      </v-data-table>
    </CardModule>

    <ModalPosTypeSettings
      v-if="posTypesModal"
      v-model="posTypesModal"
      :pos-type-data="posTypeData"
      @close="posTypesModal = false"
    />

    <ConfirmAction
      :dialog.sync="confirmDeleteModal"
      :title="$t('pos-types.delete-confirm', { name: posTypeData.description })"
      @action="confirmDeletePosType"
      @close="confirmDeleteModal = false"
    />
  </AppBody>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { formatDate, formatStatus } from "../../utils";
import AppBody from "../../layouts/components/AppBody.vue";
import CardModule from "../../components/cards/CardModule.vue";
import BaseTextField from "../../components/forms/BaseTextField.vue";
import BaseSelect from "../../components/forms/BaseSelect.vue";
import DatePicker from "../../components/forms/DatePicker.vue";
import BaseButton from "../../components/forms/BaseButton.vue";
import ExpansionPanelFilters from "../../components/forms/ExpansionPanelFilters.vue";
import ModalPosTypeSettings from "../../components/pointsOfSale/ModalPosTypeSettings.vue";
import ConfirmAction from "../../components/events/EventSettingsModal/ConfirmAction.vue";

export default {
  name: "posTypes",
  metaInfo() {
    const title = this.$t("pos-types.title");
    return {
      title: `${title} | ElevenTickets`,
      meta: [
        {
          property: "og:title",
          content: `${title} | ElevenTickets`,
        },
      ],
    };
  },
  components: {
    AppBody,
    CardModule,
    BaseTextField,
    BaseSelect,
    DatePicker,
    BaseButton,
    ExpansionPanelFilters,
    ModalPosTypeSettings,
    ConfirmAction,
  },
  data() {
    return {
      formatDate,
      formatStatus,
      posTypesModal: false,
      confirmDeleteModal: false,
      filter: {
        description: "",
        status: "",
        initialDate: "",
        finalDate: "",
      },
      posTypeData: {
        id: "",
        description: "",
        status: "",
      },
    };
  },
  computed: {
    ...mapGetters(["posTypes", "posTypesLoading"]),
    actions() {
      return [
        {
          name: "Novo Tipo de Ponto de Venda",
          action: () => this.openCreatePosTypeModal(),
        },
      ];
    },
    status() {
      return [
        {
          text: this.$t("common.active"),
          value: 1,
        },
        {
          text: this.$t("common.inactive"),
          value: 0,
        },
      ];
    },
    headers() {
      return [
        {
          text: this.$t("pos-types.description"),
          align: "start",
          sortable: true,
          value: "descricao",
          width: 140,
        },
        {
          text: this.$t("pos-types.inclusion-date"),
          align: "start",
          sortable: true,
          value: "ins_dthr",
          width: 100,
        },
        {
          text: "Status",
          align: "center",
          sortable: true,
          value: "ativo",
          width: 100,
        },
        {
          text: this.$t("common.actions"),
          align: "center",
          sortable: false,
          value: "actions",
          width: 100,
        },
      ];
    },
  },
  methods: {
    ...mapActions(["fetchPosTypes", "deletePosType"]),
    filterPosTypes() {
      let filters = [];

      if (this.filter.description) {
        filters.push({
          campo: "descricao",
          orientacao: "like",
          tipo: "string",
          valor: this.filter.description.trim(),
        });
      }
      if (this.filter.status) {
        filters.push({
          campo: "ativo",
          orientacao: "=",
          tipo: "string",
          valor: this.filter.status,
        });
      }
      if (this.filter.initialDate) {
        filters.push({
          campo: "ins_dthr",
          orientacao: "maior",
          tipo: "date",
          valor: this.filter.initialDate + " 00:00",
        });
      }
      if (this.filter.finalDate) {
        filters.push({
          campo: "ins_dthr",
          orientacao: "menor",
          tipo: "date",
          valor: this.filter.finalDate + " 23:59",
        });
      }

      this.fetchPosTypes(filters);
    },
    handleInitialDate(formattedDate) {
      this.filter.initialDate = formattedDate;
    },
    handleFinalDate(formattedDate) {
      this.filter.finalDate = formattedDate;
    },
    openCreatePosTypeModal() {
      this.posTypeData = {
        id: "",
        description: "",
        status: "",
      };

      this.posTypesModal = true;
    },
    openEditPosTypeModal(item) {
      this.posTypesModal = true;

      this.posTypeData = {
        id: item.idtipo_pdv,
        description: item.descricao,
        status: item.ativo === 1 ? "ATIVO" : "INATIVO",
      };
    },
    openDeleteModal(item) {
      this.posTypeData.id = item.idtipo_pdv;
      this.posTypeData.description = item.descricao;
      this.confirmDeleteModal = true;
    },
    confirmDeletePosType() {
      this.deletePosType(this.posTypeData.id);
      this.confirmDeleteModal = false;
    },
  },
  mounted() {
    this.fetchPosTypes();
  },
};
</script>

<style scoped></style>
